import api from './api';

class TestingLaboratoryService {
  // Base URLs
  static BASE_URL = '/testing-laboratory';

  // Get laboratory details with fallback logic
  static async getLaboratoryDetails(orgId) {
    try {
      const response = await api.get(`${this.BASE_URL}/details/${orgId}`);
      return {
        success: true,
        data: response.data.data,
        source: response.data.source,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error fetching laboratory details:', error);
      // eslint-disable-next-line no-throw-literal
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch laboratory details',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get laboratory adequacy check results
      static async getLaboratoryAdequacyCheck(appId, orgId, labTypeId) {
          try {
              const response = await api.get(`${this.BASE_URL}/adequacy-check`, {
                  params: { appId, orgId, labTypeId }
              });
              return response.data;
          } catch (error) {
              console.error('Error fetching laboratory adequacy check:', error);
              throw error;
          }
      }

  static async assessorDiscipline(assessorId, appId) {
    try {
      const response = await api.get(`${this.BASE_URL}/assessorDiscipline/${assessorId}/${appId}`);
      return {
        success: true,
        data: response.data.data,
        source: response.data.source,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error fetching assessor disciplines:', error);
      // eslint-disable-next-line no-throw-literal
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch assessor disciplines',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get basic laboratory details for binding (laboratory name, contact info, etc.)
  static async getBasicDetails(orgId) {
    try {
      const response = await api.get(`${this.BASE_URL}/basic-details/${orgId}`);
      return {
        success: true,
        data: response.data.data,
        source: response.data.source,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error fetching basic laboratory details:', error);
      // eslint-disable-next-line no-throw-literal
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch basic laboratory details',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Save laboratory details
  static async saveLaboratoryDetails(orgId, labData) {
    try {
      const response = await api.post(`${this.BASE_URL}/details/${orgId}`, labData);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error saving laboratory details:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to save laboratory details',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get all states
  static async getStates() {
    try {
      const response = await api.get(`${this.BASE_URL}/states`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error fetching states:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch states',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get cities by state ID
  static async getCitiesByState(stateId) {
    try {
      const response = await api.get(`${this.BASE_URL}/cities/${stateId}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error fetching cities:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch cities',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get districts by state code
  static async getDistrictsByState(stateCode) {
    try {
      const response = await api.get(`${this.BASE_URL}/districts/${stateCode}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error fetching districts:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch districts',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get sub-districts by district code
  static async getSubDistrictsByDistrict(districtCode) {
    try {
      const response = await api.get(`${this.BASE_URL}/sub-districts/${districtCode}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error fetching sub-districts:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch sub-districts',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Update organization documents
  static async updateOrganizationDocuments(appId, documents) {
    try {
      const response = await api.put(`${this.BASE_URL}/organization-documents/${appId}`, documents);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error updating organization documents:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to update organization documents',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get organization documents
  static async getOrganizationDocuments(appId) {
    try {
      const response = await api.get(`${this.BASE_URL}/organization-documents/${appId}`);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error fetching organization documents:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch organization documents',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get disciplines by labTypeId and disciplineType
  static async getDisciplines(labTypeId, disciplineType, orgId, appId) {
    try {
      const params = new URLSearchParams({
        labTypeId: labTypeId || '3',
        disciplineType: disciplineType || 'Diagnostic',
        orgId: orgId || '',
        appId: appId || '',
        isActive: '0'
      });
      
      const response = await api.get(`${this.BASE_URL}/disciplines?${params}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error fetching disciplines:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch disciplines',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Add discipline selection
  static async addDisciplineSelection(appId, orgId, disciplineId, groupId, insertedBy) {
    try {
      const response = await api.post(`${this.BASE_URL}/disciplines/selection`, {
        appId,
        orgId,
        disciplineId,
        groupId,
        insertedBy
      });
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error adding discipline selection:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to add discipline selection',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get selected disciplines
  static async getSelectedDisciplines(appId, orgId) {
    try {
      const params = new URLSearchParams({
        appId: appId || '',
        orgId: orgId || ''
      });
      
      const response = await api.get(`${this.BASE_URL}/disciplines/selection?${params}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error fetching selected disciplines:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch selected disciplines',
        error: error.response?.data?.error || error.message
      };
    }
  }

  static async getSelectedDisciplinesForStaff(appId, orgId) {
    try {
      const params = new URLSearchParams({
        appId: appId || "",
        orgId: orgId || "",
      });

      const response = await api.get(
        `${this.BASE_URL}/disciplines/selection-staff?${params}`,
      );
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error("Error fetching selected disciplines:", error);
      throw {
        success: false,
        message:
          error.response?.data?.message ||
          "Failed to fetch selected disciplines",
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Delete discipline selection
  static async deleteDisciplineSelection(id) {
    try {
      const response = await api.delete(`${this.BASE_URL}/disciplines/selection/${id}`);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error deleting discipline selection:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to delete discipline selection',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // ==================== LAB STAFF CRUD OPERATIONS ====================

  // Create lab staff
  static async createLabStaff(labStaffData) {
    try {
      const response = await api.post(`${this.BASE_URL}/lab-staff`, labStaffData);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error creating lab staff:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to create lab staff',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get lab staff
  static async getLabStaff(appId, orgId) {
    try {
      const params = new URLSearchParams({
        appId: appId || '',
        orgId: orgId || ''
      });
      
      const response = await api.get(`${this.BASE_URL}/lab-staff?${params}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error fetching lab staff:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch lab staff',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get lab staff with deleted
    static async getLabStaffWithDeleted(appId, orgId) {
      try {
        const params = new URLSearchParams({
          appId: appId || '',
          orgId: orgId || ''
        });
        
        const response = await api.get(`${this.BASE_URL}/lab-staff/with-deleted?${params}`);
        return {
          success: true,
          data: response.data.data
        };
      } catch (error) {
        console.error('Error fetching lab staff with deleted:', error);
        throw {
          success: false,
          message: error.response?.data?.message || 'Failed to fetch lab staff',
          error: error.response?.data?.error || error.message
        };
      }
    }

  // Update lab staff
  static async updateLabStaff(id, labStaffData) {
    try {
      const response = await api.put(`${this.BASE_URL}/lab-staff/${id}`, labStaffData);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error updating lab staff:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to update lab staff',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Delete lab staff
  static async deleteLabStaff(id) {
    try {
      const response = await api.delete(`${this.BASE_URL}/lab-staff/${id}`);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error deleting lab staff:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to delete lab staff',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get scope master data with pagination
  static async getScopeMaster(labTypeId, disciplineId, groupId, page = 1, limit = 10, search = '') {
    try {
      const params = new URLSearchParams({
        labTypeId,
        disciplineId,
        groupId,
        page: page.toString(),
        limit: limit.toString()
      });

      if (search) {
        params.append('search', search);
      }

      const response = await api.get(`${this.BASE_URL}/scope-master?${params}`);
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error fetching scope master data:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch scope master data',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Save selected scopes to form153Scope table
  static async saveSelectedScopes(selectedScopes, appId, orgId, disciplineId, groupId,labTypeId) {
    try {
      const response = await api.post(`${this.BASE_URL}/scope/save-selected`, {
        selectedScopes,
        appId,
        orgId,
        disciplineId,
        groupId,
        labTypeId
      });
      return {
        success: true,
        message: response.data.message,
        count: response.data.count
      };
    } catch (error) {
      console.error('Error saving selected scopes:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to save selected scopes',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get existing scopes from form153Scope table
  static async getExistingScopes(appId, orgId, disciplineId, groupId, page = 1, pageSize = 10, search = '') {
    try {
      const params = new URLSearchParams({
        appId,
        orgId,
        disciplineId,
        groupId,
        page: page.toString(),
        pageSize: pageSize.toString(),
        search: search.trim()
      });

      const response = await api.get(`${this.BASE_URL}/scope/existing?${params}`);
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error fetching existing scopes:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch existing scopes',
        error: error.response?.data?.error || error.message
      };
    }
  }

   // Get existing scopes from form153Scope table with deleted
    static async getExistingScopesWithDeleted(appId, orgId, disciplineId, groupId, page = 1, pageSize = 10, search = '') {
      try {
        const params = new URLSearchParams({
          appId,
          orgId,
          disciplineId,
          groupId,
          page: page.toString(),
          pageSize: pageSize.toString(),
          search: search.trim()
        });
  
        const response = await api.get(`${this.BASE_URL}/scope/existing-with-deleted?${params}`);
        return {
          success: true,
          data: response.data.data,
          pagination: response.data.pagination,
          message: response.data.message
        };
      } catch (error) {
        console.error('Error fetching existing scopes with deleted:', error);
        throw {
          success: false,
          message: error.response?.data?.message || 'Failed to fetch existing scopes with deleted',
          error: error.response?.data?.error || error.message
        };
      }
    }

  // Get existing scopes from form153Scope table
  static async getExistingScopesAssessment(
    appId,
    orgId,
    disciplineId,
    groupId,
    assessorId,
    assessmentId,
    page = 1,
    pageSize = 10,
    search = "",
  ) {
    try {
      const params = new URLSearchParams({
        appId,
        orgId,
        disciplineId,
        groupId,
        assessorId,
        assessmentId,
        page: page.toString(),
        pageSize: pageSize.toString(),
        search: search.trim(),
      });

      const response = await api.get(
        `${this.BASE_URL}/scope/existing-assessment?${params}`,
      );
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination,
        message: response.data.message,
      };
    } catch (error) {
      console.error("Error fetching existing scopes:", error);
      throw {
        success: false,
        message:
          error.response?.data?.message || "Failed to fetch existing scopes",
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Delete existing scope
  static async deleteExistingScope(scopeId) {
    try {
      const response = await api.delete(`${this.BASE_URL}/scope/${scopeId}`);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error deleting scope:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to delete scope',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Update existing scope
  static async updateExistingScope(scopeId, rangeRemark, rangeType, RangeLL, RangeLLUnit, RangeUL, RangeULUnit, mu, cv, mv, testPerformedAt, EquipmentName, Ref_Id, Pt_id) {
    try {
      const updateData = {
        rangeRemark,
        rangeType,
        RangeLL,
        RangeLLUnit,
        RangeUL,
        RangeULUnit,
        mu,
        cv,
        mv,
        testPerformedAt,
        EquipmentName,
        Ref_Id,
        Pt_id
      };
      
      const response = await api.put(`${this.BASE_URL}/scope/${scopeId}`, updateData);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error updating scope:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to update scope',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Mark scopes for test witness
  static async markTestWitness(data) {
    try {
      const response = await api.post(`${this.BASE_URL}/scope/mark-test-witness`, data);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error marking scopes for test witness:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to mark scopes for test witness',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Unmark test witness for a single scope
  static async unmarkTestWitness(scopeId, appId, orgId) {
    try {
      const response = await api.put(`${this.BASE_URL}/scope/unmark-test-witness/${scopeId}`, {
        appId,
        orgId
      });
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error unmarking test witness:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to unmark test witness',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Equipment Management Methods

  // Get equipment master data
  static async getEquipmentMaster(labTypeId, disciplineId, groupId, page = 1, pageSize = 10, search = '') {
    try {
      const params = new URLSearchParams({
        labTypeId,
        disciplineId,
        groupId,
        page,
        pageSize,
        search
      });
      
      const response = await api.get(`${this.BASE_URL}/equipment-master?${params}`);
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Error fetching equipment master data:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch equipment master data',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get equipment master data for dropdown (no pagination)
  static async getEquipmentMasterForDropdown(labTypeId, disciplineId, groupId, appId, orgId) {
    try {
      const params = new URLSearchParams({
        appId,
        orgId
      });
      
      const response = await api.get(`${this.BASE_URL}/equipment-master-dropdown/${labTypeId}/${disciplineId}/${groupId}?${params}`);
      return {
        success: true,
        data: response.data.data,
        count: response.data.count,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error fetching equipment master dropdown data:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch equipment master dropdown data',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Save selected equipments to Form153EquipmentDetails
  static async saveSelectedEquipments(selectedEquipments, appId, orgId, disciplineId, groupId,labTypeId) {
    try {
      const response = await api.post(`${this.BASE_URL}/equipment/save-selected`, {
        equipments: selectedEquipments,
        appId,
        orgId,
        disciplineId,
        groupId,
        labTypeId
      });
      return {
        success: true,
        message: response.data.message,
        count: response.data.count
      };
    } catch (error) {
      console.error('Error saving selected equipments:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to save selected equipments',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get existing equipments from Form153EquipmentDetails
  static async getExistingEquipments(appId, orgId, disciplineId,groupId) {
    try {
      const params = new URLSearchParams({
        appId,
        orgId,
        disciplineId,
        groupId
      });
      
      const response = await api.get(`${this.BASE_URL}/equipment/existing?${params}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error fetching existing equipments:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch existing equipments',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get existing equipments from Form153EquipmentDetails with deleted
    static async getExistingEquipmentsWithDeleted(appId, orgId, disciplineId, groupId) {
      try {
        const params = new URLSearchParams({
          appId,
          orgId,
          disciplineId,
          groupId
        });
        
        const response = await api.get(`${this.BASE_URL}/equipment/existing-with-deleted?${params}`);
        return {
          success: true,
          data: response.data.data
        };
      } catch (error) {
        console.error('Error fetching existing equipments with deleted:', error);
        throw {
          success: false,
          message: error.response?.data?.message || 'Failed to fetch existing equipments with deleted',
          error: error.response?.data?.error || error.message
        };
      }
    }

  static async getexistingallEquipments(appId, orgId) {
    try {      
      const response = await api.get(`${this.BASE_URL}/equipment/existingall?appId=${appId}&orgId=${orgId}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error fetching existing equipments:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch existing equipments',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Update existing equipment
  static async updateExistingEquipment(equipmentId, formData) {
    try {
      const response = await api.put(`${this.BASE_URL}/equipment/${equipmentId}`, formData);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error updating equipment:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to update equipment',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Delete existing equipment
  static async deleteExistingEquipment(equipmentId) {
    try {
      const response = await api.delete(`${this.BASE_URL}/equipment/${equipmentId}`);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error deleting equipment:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to delete equipment',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Save other equipment (not from master)
  static async saveEquipment(equipmentData) {
    try {
      const response = await api.post(`${this.BASE_URL}/equipment/save-other`, equipmentData);
      return {
        success: true,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error saving equipment:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to save equipment',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Save other scope (not from master)
  static async saveOtherScope(scopeData) {
    try {
      const response = await api.post(`${this.BASE_URL}/scope/save-other`, scopeData);
      return {
        success: true,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error saving other scope:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to save other scope',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Bulk update existing scopes
  static async bulkUpdateScopes(updateData) {
    try {
      const response = await api.put(`${this.BASE_URL}/scope/bulk-update`, updateData);
      return {
        success: true,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error bulk updating scopes:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to update scopes',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Reference Material Methods
  
  // Get reference materials for a discipline
  static async getReferenceMaterials(appId, orgId, disciplineId, groupId) {
    try {
      const response = await api.get(`${this.BASE_URL}/reference-materials`, {
        params: { appId, orgId, disciplineId, groupId }
      });
      return {
        success: true,
        data: response.data.data || []
      };
    } catch (error) {
      console.error('Error fetching reference materials:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch reference materials',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get reference materials for a discipline with deleted
    static async getReferenceMaterialsWithDeleted(appId, orgId, disciplineId, groupId) {
      try {
        const response = await api.get(`${this.BASE_URL}/reference-materials-with-deleted`, {
          params: { appId, orgId, disciplineId, groupId }
        });
        return {
          success: true,
          data: response.data.data || []
        };
      } catch (error) {
        console.error('Error fetching reference materials with deleted:', error);
        throw {
          success: false,
          message: error.response?.data?.message || 'Failed to fetch reference materials with deleted',
          error: error.response?.data?.error || error.message
        };
      }
    }
    
 static async getScopeReferenceDetailsById(appId, orgId, Id) {
  try {
    const response = await api.get(
      `${this.BASE_URL}/reference-materialsbyId`,
      {
        params: { appId, orgId, Id }   // ✅ FIX HERE
      }
    );

    return {
      success: true,
      data: response.data.data || []
    };
  } catch (error) {
    console.error('Error fetching reference materials:', error);
    throw {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch reference materials',
      error: error.response?.data?.error || error.message
    };
  }
}

  static async getScopePtDetailsById(appId, orgId, Id) {
    try {
      const response = await api.get(`${this.BASE_URL}/proficiency-testingbyId`, {
        params: { appId, orgId, Id }
      });
      return {
        success: true,
        data: response.data.data || []
      };
    } catch (error) {
      console.error('Error fetching reference materials:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch reference materials',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Add new reference material
  static async addReferenceMaterial(materialData) {
    try {
      const response = await api.post(`${this.BASE_URL}/reference-materials`, materialData);
      return {
        success: true,
        message: response.data.message || 'Reference material added successfully',
        data: response.data.data
      };
    } catch (error) {
      console.error('Error adding reference material:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to add reference material',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Update reference material
  static async updateReferenceMaterial(materialId, materialData) {
    try {
      const response = await api.put(`${this.BASE_URL}/reference-materials/${materialId}`, materialData);
      return {
        success: true,
        message: response.data.message || 'Reference material updated successfully',
        data: response.data.data
      };
    } catch (error) {
      console.error('Error updating reference material:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to update reference material',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Delete reference material
  static async deleteReferenceMaterial(materialId) {
    try {
      const response = await api.delete(`${this.BASE_URL}/reference-materials/${materialId}`);
      return {
        success: true,
        message: response.data.message || 'Reference material deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting reference material:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to delete reference material',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Bulk add reference materials
  static async bulkAddReferenceMaterials(materialsData) {
    try {
      const response = await api.post(`${this.BASE_URL}/reference-materials/bulk`, { materials: materialsData });
      return {
        success: true,
        message: response.data.message || 'Reference materials added successfully',
        data: response.data.data
      };
    } catch (error) {
      console.error('Error adding bulk reference materials:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to add reference materials',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // PT Programme Methods
  
  // Get PT programmes for a discipline
  static async getPTProgrammes(appId, orgId, disciplineId, groupId) {
    try {
      const response = await api.get(`${this.BASE_URL}/pt-programmes`, {
        params: { appId, orgId, disciplineId, groupId }
      });
      return {
        success: true,
        data: response.data.data || []
      };
    } catch (error) {
      console.error('Error fetching PT programmes:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch PT programmes',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Binding Methods
  
  // Bind equipment, reference materials, and PT programmes to scopes
  static async bindScopeData(bindingData) {
    try {
      const response = await api.post(`${this.BASE_URL}/scope/bind-data`, bindingData);
      return {
        success: true,
        message: response.data.message || 'Data bound to scopes successfully',
        data: response.data.data
      };
    } catch (error) {
      console.error('Error binding scope data:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to bind data to scopes',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Phantom Management Methods

  // Get all phantoms for an application/organization
  static async getPhantoms(appId, orgId, disciplineId = null) {
    try {
      const params = { appId, orgId };
      if (disciplineId) {
        params.disciplineId = disciplineId;
      }

      const response = await api.get(`${this.BASE_URL}/phantoms`, { params });
      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message
      };
    } catch (error) {
      console.error('Error fetching phantoms:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch phantoms',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Add new phantom
  static async addPhantom(phantomData) {
    try {
      const response = await api.post(`${this.BASE_URL}/phantoms`, phantomData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Phantom added successfully'
      };
    } catch (error) {
      console.error('Error adding phantom:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to add phantom',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Update existing phantom
  static async updatePhantom(phantomId, phantomData) {
    try {
      const response = await api.put(`${this.BASE_URL}/phantoms/${phantomId}`, phantomData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Phantom updated successfully'
      };
    } catch (error) {
      console.error('Error updating phantom:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to update phantom',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Delete phantom
  static async deletePhantom(phantomId, updatedById = null, clientIp = null) {
    try {
      const response = await api.delete(`${this.BASE_URL}/phantoms/${phantomId}`, {
        data: { updatedById, clientIp }
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Phantom deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting phantom:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to delete phantom',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get phantom by ID
  static async getPhantomById(phantomId) {
    try {
      const response = await api.get(`${this.BASE_URL}/phantoms/${phantomId}`);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error fetching phantom by ID:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch phantom',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get phantom types from PhantomType table
  static async getPhantomTypes() {
    try {
      const response = await api.get(`${this.BASE_URL}/phantom-types`);
      return {
        success: true,
        data: response.data.data || []
      };
    } catch (error) {
      console.error('Error fetching phantom types:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch phantom types',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get type of test options from form153Scope table
  static async getTypeOfTestOptions(appId, orgId, disciplineId) {
    try {
      const params = { appId, orgId, disciplineId };
      const response = await api.get(`${this.BASE_URL}/type-of-test-options`, { params });
      return {
        success: true,
        data: response.data.data || []
      };
    } catch (error) {
      console.error('Error fetching type of test options:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch type of test options',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Radioisotopes/Test Kits Management Methods

  // Get all radioisotopes for an application/organization
  static async getRadioisotopes(appId, orgId, disciplineId = null) {
    try {
      const params = { appId, orgId };
      if (disciplineId) {
        params.disciplineId = disciplineId;
      }
      
      const response = await api.get(`${this.BASE_URL}/radioisotopes`, { params });
      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message
      };
    } catch (error) {
      console.error('Error fetching radioisotopes:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch radioisotopes',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Add new radioisotope/test kit
  static async addRadioisotope(radioisotopeData) {
    try {
      const response = await api.post(`${this.BASE_URL}/radioisotopes`, radioisotopeData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error adding radioisotope:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to add radioisotope',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Update existing radioisotope/test kit
  static async updateRadioisotope(radioisotopeId, radioisotopeData) {
    try {
      const response = await api.put(`${this.BASE_URL}/radioisotopes/${radioisotopeId}`, radioisotopeData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error updating radioisotope:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to update radioisotope',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Delete radioisotope/test kit
  static async deleteRadioisotope(radioisotopeId, updatedById = null, clientIp = null) {
    try {
      const requestData = {
        updatedById: updatedById || localStorage.getItem('userId'),
        clientIp: clientIp || '127.0.0.1'
      };
      
      const response = await api.delete(`${this.BASE_URL}/radioisotopes/${radioisotopeId}`, { data: requestData });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error deleting radioisotope:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to delete radioisotope',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get radioisotope by ID
  static async getRadioisotopeById(radioisotopeId) {
    try {
      const response = await api.get(`${this.BASE_URL}/radioisotopes/${radioisotopeId}`);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error fetching radioisotope by ID:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch radioisotope',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get radioisotope master data from Radioisotopes table
  static async getRadioisotopeMasterData() {
    try {
      const response = await api.get(`${this.BASE_URL}/radioisotope-master-data`);
      return {
        success: true,
        data: response.data.data || []
      };
    } catch (error) {
      console.error('Error fetching radioisotope master data:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch radioisotope master data',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Declaration Management Methods

  // Save declaration data
  static async saveDeclarationData(orgId, appId, declarationData) {
    try {
      const response = await api.post(`${this.BASE_URL}/declaration/${orgId}/${appId}`, declarationData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error saving declaration data:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to save declaration data',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get declaration data
  static async getDeclarationData(orgId, appId) {
    try {
      const response = await api.get(`${this.BASE_URL}/declaration/${orgId}/${appId}`);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error fetching declaration data:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch declaration data',
        error: error.response?.data?.error || error.message
      };
    }
  }

  static async getPaymentBasicDetail(appId) {
    try {
      const response = await api.get(`${this.BASE_URL}/basic-details-payment/${appId}`);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error fetching payment basic detail:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch payment basic detail',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get reference materials by IDs
  static async getReferenceMaterialsById({ appId, orgId, Id }) {
    try {
      const params = new URLSearchParams({
        appId: appId || '',
        orgId: orgId || '',
        Id: Id || ''
      });

      const response = await api.get(`${this.BASE_URL}/reference-materialsbyId?${params.toString()}`);
      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message || 'Reference materials fetched successfully'
      };
    } catch (error) {
      console.error('Error fetching reference materials:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch reference materials',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get proficiency testing details by scope PT main ID
  static async getScopePtDetailsById(appId, orgId, ptMainId) {
    try {
      const params = new URLSearchParams({
        appId: appId || '',
        orgId: orgId || '',
        ptMainId: ptMainId || ''
      });

      const response = await api.get(`${this.BASE_URL}/proficiency-testingbyId?${params.toString()}`);
      return {
        data: {
          success: true,
          data: response.data.data || []
        }
      };
    } catch (error) {
      console.error('Error fetching PT details:', error);
      return {
        data: {
          success: false,
          data: [],
          message: error.response?.data?.message || 'Failed to fetch PT details'
        }
      };
    }
  }

  // Get equipment details by equipment IDs
  static async getEquipmentDetails({ appId, orgId, equipmentIds }) {
    try {
      const params = new URLSearchParams({
        appId: appId || '',
        orgId: orgId || '',
        equipmentIds: equipmentIds || ''
      });

      const response = await api.get(`${this.BASE_URL}/equipment-details-assessment?${params.toString()}`);
      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message || 'Equipment details fetched successfully'
      };
    } catch (error) {
      console.error('Error fetching equipment details:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch equipment details',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // ==================== PERFORMANCE SCORE MANAGEMENT ====================

  // Add performance score
  static async addPerformanceScore(payload) {
    try {
      const response = await api.post(`${this.BASE_URL}/performance-score`, payload);
      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message || 'Performance score added successfully'
      };
    } catch (error) {
      console.error('Error adding performance score:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to add performance score',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Get performance scores by labTypeId and scopeId
  static async getPerformanceScores({ labTypeId, scopeId }) {
    try {
      const params = new URLSearchParams({
        labTypeId: labTypeId || '',
        scopeId: scopeId || ''
      });

      const response = await api.get(`${this.BASE_URL}/performance-score?${params.toString()}`);
      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message || 'Performance scores retrieved successfully'
      };
    } catch (error) {
      console.error('Error fetching performance scores:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch performance scores',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Update performance score
  static async updatePerformanceScore(id, payload) {
    try {
      const response = await api.put(`${this.BASE_URL}/performance-score/${id}`, payload);
      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message || 'Performance score updated successfully'
      };
    } catch (error) {
      console.error('Error updating performance score:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to update performance score',
        error: error.response?.data?.error || error.message
      };
    }
  }

  // Delete performance score
  static async deletePerformanceScore(id) {
    try {
      const response = await api.delete(`${this.BASE_URL}/performance-score/${id}`);
      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message || 'Performance score deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting performance score:', error);
      throw {
        success: false,
        message: error.response?.data?.message || 'Failed to delete performance score',
        error: error.response?.data?.error || error.message
      };
    }
  }
}

export default TestingLaboratoryService;
